import { useState, useEffect } from 'react'
import { api } from '../lib/api'
export const ALL_SECTIONS = [
  { id: 'dashboard',  icon: '🏠', label: 'Dashboard',          actions: ['view'] },
  { id: 'products',   icon: '◈', label: 'Product Research', actions: ['view','edit','create','delete','generate','resources'] },
  { id: 'funnel',     icon: '⬡', label: 'Funnel Building',  actions: ['view','edit','create','delete','audit','wetracked','checklist','resources'] },
  { id: 'creatives',  icon: '◐', label: 'Creatives',        actions: ['view','edit','create','delete','resources'] },
  { id: 'assetprep',  icon: '🎨', label: 'Asset Prep',       actions: ['view','edit','create','delete'] },
  { id: 'adcopy',     icon: '◻', label: 'Ad Copy',          actions: ['view','edit','create','delete','adapt','resources'] },
  { id: 'scheduling', icon: '◷', label: 'Ad Scheduling',    actions: ['view','edit','create','delete','resources'] },
  { id: 'winning',    icon: '◆', label: 'Winning Products', actions: ['view','edit','create','delete','resources'] },
  { id: 'creativerequests', icon: '🎬', label: 'Creative Requests', actions: ['view','edit','create','delete'] },
  { id: 'fbinfra', icon: '🌐', label: 'FB Infrastructure', actions: ['view','edit','create','delete'] },
  { id: 'shopifyorders', icon: '🛒', label: 'Shopify Orders', actions: ['view','edit'] },
  { id: 'reports',    icon: '📊', label: 'Reports',          actions: ['view'] },
  { id: 'company',    icon: '🏢', label: 'Company',          actions: ['view'] },
  { id: 'payroll',    icon: '💵', label: 'Payroll',          actions: ['view','edit','create','delete'] },
  { id: 'activitylog', icon: '📋', label: 'Activity Log',      actions: ['view'] },
  { id: 'pricecalc',  icon: '🧮', label: 'Price Calculator',   actions: ['view'] },
  { id: 'pricingagent', icon: '🤖', label: 'Pricing Agent',    actions: ['view'] },
  { id: 'changelog',  icon: '📢', label: "What's New",        actions: ['view'] },
]

export const ACTION_LABELS = {
  view:'View', edit:'Edit', create:'Create', delete:'Delete',
  generate:'Generate', audit:'Audit', wetracked:'Wetracked', checklist:'Checklist', adapt:'Adapt', resources:'Resources',
}

// Check if a user can access a section (has at least view)
export function canSeeSection(perms, sectionId, isOwner) {
  if (isOwner) return true
  const p = perms?.[sectionId]
  if (p === true) return true            // legacy boolean
  if (typeof p === 'object' && p) return !!p.view || Object.values(p).some(Boolean)
  return false
}

// Check a specific action
export function can(perms, sectionId, action, isOwner) {
  if (isOwner) return true
  const p = perms?.[sectionId]
  if (p === true) return true            // legacy boolean = all actions
  if (typeof p === 'object' && p) return !!p[action]
  return false
}

// Does user have general resources access?
export function hasGeneralResources(perms, isOwner) {
  if (isOwner) return true
  if (!perms) return false
  const p = perms['general']
  if (p === true) return true
  return typeof p === 'object' && p && !!p.view
}

// Does user have resources access to at least one section?
export function hasAnyResources(perms, isOwner) {
  if (isOwner) return true
  if (!perms) return false
  return ALL_SECTIONS.some(s => {
    const p = perms[s.id]
    if (p === true) return true
    return typeof p === 'object' && p && !!p.resources
  })
}


function useBadges() {
  const [badges, setBadges] = useState({})
  useEffect(() => {
    let alive = true
    const fetchBadges = () => {
      api.getDashboard().then(d => { if (alive) setBadges(d?.badges || {}) }).catch(() => {})
    }
    fetchBadges()
    const t = setInterval(fetchBadges, 120000) // refresh every 2 min
    return () => { alive = false; clearInterval(t) }
  }, [])
  return badges
}

export default function Sidebar({ active, setActive, effectivePerms, isOwner }) {
  const visible = isOwner
    ? ALL_SECTIONS
    : ALL_SECTIONS.filter(s => s.id === 'dashboard' || s.id === 'changelog' || (s.id !== 'company' && canSeeSection(effectivePerms, s.id, false)))

  const badges = useBadges()
  return (
    <div className="sidebar">
      <div className="sidebar-section">
        <div className="sidebar-label">Sections</div>
        {visible.map(s => (
          <button key={s.id}
            className={`sidebar-item ${active === s.id ? 'active' : ''}`}
            onClick={() => setActive(s.id)}>
            <span className="icon" style={{fontFamily:'monospace',fontSize:13}}>{s.icon}</span>
            <span style={{flex:1,textAlign:'left'}}>{s.label}</span>
            {badges[s.id] > 0 && <span style={{fontSize:10,fontWeight:700,color:'#fff',background:'var(--accent)',borderRadius:10,padding:'1px 6px',minWidth:18,textAlign:'center'}}>{badges[s.id]}</span>}
          </button>
        ))}
        {hasAnyResources(effectivePerms, isOwner) && (
          <button
            className={`sidebar-item ${active === 'resources' ? 'active' : ''}`}
            onClick={() => setActive('resources')}>
            <span className="icon" style={{fontFamily:'monospace',fontSize:13}}>📚</span>
            Resources
          </button>
        )}
        {hasGeneralResources(effectivePerms, isOwner) && (
          <button
            className={`sidebar-item ${active === 'general' ? 'active' : ''}`}
            onClick={() => setActive('general')}>
            <span className="icon" style={{fontFamily:'monospace',fontSize:13}}>📦</span>
            General Resources
          </button>
        )}
        {!isOwner && visible.length === 0 && !hasAnyResources(effectivePerms, false) && (
          <div style={{padding:'8px 16px',fontSize:12,color:'var(--text3)'}}>No sections assigned</div>
        )}
      </div>
      {isOwner && (
        <div className="sidebar-section" style={{borderTop:'1px solid var(--border)',marginTop:'auto'}}>
          <div className="sidebar-label">Admin</div>
          <button className={`sidebar-item ${active === 'users' ? 'active' : ''}`} onClick={() => setActive('users')}>
            <span className="icon">👥</span> Users
          </button>
          <button className={`sidebar-item ${active === 'settings' ? 'active' : ''}`} onClick={() => setActive('settings')}>
            <span className="icon">⚙️</span> Settings
          </button>
        </div>
      )}
    </div>
  )
}
